// @flow

import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import * as Sentry from '@sentry/react';
import isEmpty from 'lodash/isEmpty';
import {useApi, useCookies, useLocation} from 'docs/data';
import {contextStubFunction, removeNulls} from 'docs/util';

import type {
  Prefs,
  GetPrefFunction,
  SetPrefFunction,
  SetPrefsFunction,
} from 'docs/data';

const PREFS_COOKIE_NAME = 'docs.prefs';

// The following prefs are persisted in a cookie. Other prefs can be set, but they
// will only persist for the current session.
const COOKIE_PREFS = [
  'platform',
  'lang',
  'client',
  'reader',
  'elements-or-checkout',
  'integration-country',
  'nameserver-provider', // Used for Checkout custom domains docs
];

export type PrefsContextType = {|
  getPref: GetPrefFunction,
  setPref: SetPrefFunction,
  setPrefs: SetPrefsFunction,
|};

export const PrefsContext = React.createContext<PrefsContextType>({
  getPref: contextStubFunction('Prefs'),
  setPref: contextStubFunction('Prefs'),
  setPrefs: contextStubFunction('Prefs'),
});

type PrefsProviderProps = {|
  children: React.Node,
  prefs: Prefs,
|};

export const PrefsProvider = (props: PrefsProviderProps) => {
  const [prefs, savePrefsToState] = useState(props.prefs);
  const {api} = useApi();
  const {cookies, getCookieDomain} = useCookies();
  const {location, replaceLocation} = useLocation();

  const savePrefsToCookie = (newPrefs: Prefs) => {
    const cookiePrefs = COOKIE_PREFS.reduce((hash, name) => {
      if (newPrefs[name]) {
        hash[name] = newPrefs[name];
      }
      return hash;
    }, {});
    cookies.set(PREFS_COOKIE_NAME, JSON.stringify(cookiePrefs), {
      domain: getCookieDomain(),
    });
  };

  const savePrefsToQueryString = (newPrefs: Prefs) => {
    // We only want to update the query string if it already contains a
    // value for the pref. (This ensures that if the user navigates to a new page
    // and then back, the query string in the URL won't override the pref value
    // stored in state.)
    const query = Object.keys(location.query).reduce(
      (hash, name) => {
        if (
          newPrefs[name] !== location.query[name] &&
          newPrefs[name] !== undefined
        ) {
          hash[name] = newPrefs[name];
        }
        return hash;
      },
      {...location.query},
    );

    if (!isEmpty(query)) {
      replaceLocation({...location, query});
    }
  };

  function setPrefs(changedPrefs: Prefs) {
    const newPrefs = removeNulls({...prefs, ...changedPrefs});
    savePrefsToState(newPrefs);
    savePrefsToCookie(newPrefs);
    savePrefsToQueryString(changedPrefs);
    api.savePrefs(changedPrefs).catch((err) => {
      if (Sentry && Sentry.captureMessage) {
        Sentry.captureMessage(`Failed to load set global user preferences`);
      }
    });
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[Prefs] New prefs: %o', newPrefs);
    }
  }

  const prefsController = {
    getPref: (name: string) => {
      return prefs[name] === undefined ? null : prefs[name];
    },
    setPref: <T>(name: string, value: T) => {
      return setPrefs({[name]: value});
    },
    setPrefs,
  };

  // On the first load, make sure that changes from the query string
  // are written to the cookie.
  useEffect(
    () => savePrefsToCookie(prefs),
    // TODO: use correct dependencies
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [],
  );

  // When the query string changes, update the prefs to match.
  useEffect(() => {
    const changes = Object.keys(location.query).reduce((hash, name) => {
      if (prefs[name] !== location.query[name]) {
        hash[name] = location.query[name];
      }
      return hash;
    }, {});

    if (!isEmpty(changes)) {
      prefsController.setPrefs(changes);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [location.query]);

  return (
    <PrefsContext.Provider value={prefsController}>
      {props.children}
    </PrefsContext.Provider>
  );
};

export const usePrefs = () => {
  return useContext(PrefsContext);
};
